import { subscriberEmailStatus } from './subscriberEmailStatus';
import type { PostmarkOutboundMessage } from './postmarkOutboundMessages';
import type { ScheduledEmailRow, SubscriberRow } from '../components/Subscribers/types';

/** Postmark Tag on subscriber newsletter invite sends — keep in sync with engine-ui/supabase/functions/_shared/newsletterInviteTag.ts */
export const NEWSLETTER_INVITE_POSTMARK_TAG = 'newsletter-invite';

export type NewsletterInviteSendRecord = {
  sentAt: string;
  /** postmark = direct transactional send; scheduled_emails = CRM log row (fallback) */
  source: 'postmark' | 'scheduled_emails';
};

function normalizePostmarkTag(tag: string | null | undefined): string {
  return (tag ?? '').trim().toLowerCase();
}

export function hasNewsletterInvitePostmarkTag(tag: string | null | undefined): boolean {
  return normalizePostmarkTag(tag) === NEWSLETTER_INVITE_POSTMARK_TAG;
}

function postmarkMessageIdFromScheduledRow(row: ScheduledEmailRow): string | null {
  const direct = (row as { postmark_message_id?: string | null }).postmark_message_id;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const provider = (row.provider ?? '').trim();
  if (provider.startsWith('postmark:')) {
    const id = provider.slice('postmark:'.length).trim();
    return id || null;
  }
  return null;
}

/** Match invite sends in Postmark outbound list — tag is canonical; CRM log linkage is fallback. */
export function isPostmarkNewsletterInviteMessage(
  message: PostmarkOutboundMessage,
  scheduledInviteRows: ScheduledEmailRow[] = [],
): boolean {
  if (hasNewsletterInvitePostmarkTag(message.tag)) return true;

  for (const row of scheduledInviteRows) {
    const linkedId = postmarkMessageIdFromScheduledRow(row);
    if (linkedId && linkedId === message.messageId) return true;
  }

  return false;
}

/**
 * Whether the subscriber list should show an "Invite" action / needs-invite filter.
 * Default: CRM-only contacts (`email_status = raw`) not yet invited.
 */
export function needsNewsletterInvite(row: SubscriberRow | null | undefined): boolean {
  if (!row) return false;
  return subscriberEmailStatus(row) === 'raw';
}

/** Raw or invited — can send / resend the newsletter invite email. */
export function canSendNewsletterInvite(row: SubscriberRow | null | undefined): boolean {
  if (!row) return false;
  const status = subscriberEmailStatus(row);
  return status === 'raw' || status === 'invited';
}

export function needsInviteFilterActive(filter: string): boolean {
  return filter === 'needs_invite';
}

/** CRM log rows for llc-send-subscriber-invite (inserted after direct Postmark send). */
export function isNewsletterInviteSend(row: ScheduledEmailRow): boolean {
  if (row.status !== 'sent') return false;
  if (hasNewsletterInvitePostmarkTag(row.postmark_tag)) return true;
  const name = (row.name ?? '').trim().toLowerCase();
  const alias = (row.postmark_template_alias ?? '').trim().toLowerCase();
  if (name === 'newsletter invite' || alias === 'invite') return true;
  const body = (row.body_html ?? '').toLowerCase();
  return body.includes('transactional:invite');
}

/** Load Postmark outbound messages tagged newsletter-invite (API filter, then client-side tag match). */
export async function loadNewsletterInvitePostmarkMessages(
  loadMessages: (
    email: string,
    opts?: { count?: number; tag?: string },
  ) => Promise<{ messages: PostmarkOutboundMessage[] }>,
  email: string,
  count = 50,
): Promise<PostmarkOutboundMessage[]> {
  const trimmed = email.trim();
  if (!trimmed) return [];

  const tagged = await loadMessages(trimmed, { tag: NEWSLETTER_INVITE_POSTMARK_TAG, count });
  if (tagged.messages.length > 0) return tagged.messages;

  const recent = await loadMessages(trimmed, { count });
  return recent.messages.filter((m) => hasNewsletterInvitePostmarkTag(m.tag));
}

export function newsletterInviteSendsFromScheduled(rows: ScheduledEmailRow[]): NewsletterInviteSendRecord[] {
  return newsletterInviteSends(rows).map((row) => ({
    sentAt: row.sent_at ?? row.scheduled_at ?? '',
    source: 'scheduled_emails' as const,
  }));
}

export function newsletterInviteSends(rows: ScheduledEmailRow[]): ScheduledEmailRow[] {
  return rows
    .filter(isNewsletterInviteSend)
    .sort((a, b) => {
      const ta = new Date(a.sent_at ?? a.scheduled_at ?? 0).getTime();
      const tb = new Date(b.sent_at ?? b.scheduled_at ?? 0).getTime();
      return tb - ta;
    });
}

export function newsletterInviteSendsFromPostmark(
  messages: PostmarkOutboundMessage[],
  scheduledInviteRows: ScheduledEmailRow[] = [],
): NewsletterInviteSendRecord[] {
  return messages
    .filter((m) => isPostmarkNewsletterInviteMessage(m, scheduledInviteRows))
    .map((m) => ({
      sentAt: m.receivedAt,
      source: 'postmark' as const,
    }))
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
}

/** Merge Postmark outbound history with scheduled_emails CRM log rows (deduped). */
export function mergeNewsletterInviteSendHistory(
  postmarkMessages: PostmarkOutboundMessage[],
  scheduledRows: ScheduledEmailRow[],
): NewsletterInviteSendRecord[] {
  const scheduledInviteRows = newsletterInviteSends(scheduledRows);

  type Internal = NewsletterInviteSendRecord & { dedupeKey: string };
  const combined: Internal[] = [];

  for (const m of postmarkMessages) {
    if (!isPostmarkNewsletterInviteMessage(m, scheduledInviteRows)) continue;
    combined.push({
      sentAt: m.receivedAt,
      source: 'postmark',
      dedupeKey: m.messageId || m.receivedAt,
    });
  }

  for (const row of scheduledInviteRows) {
    const sentAt = row.sent_at ?? row.scheduled_at ?? '';
    if (!sentAt) continue;
    combined.push({
      sentAt,
      source: 'scheduled_emails',
      dedupeKey: postmarkMessageIdFromScheduledRow(row) ?? row.id,
    });
  }

  const seen = new Set<string>();
  return combined
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
    .filter((r) => {
      if (seen.has(r.dedupeKey)) return false;
      seen.add(r.dedupeKey);
      return true;
    })
    .map(({ sentAt, source }) => ({ sentAt, source }));
}
